import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Spin, Input, Button, message, Tag } from 'antd';
import {
  SendOutlined,
  BarChartOutlined,
  LineChartOutlined,
  ExperimentOutlined,
  DotChartOutlined,
  ToolOutlined,
  FundOutlined,
  ClusterOutlined,
  FieldTimeOutlined,
  HeatMapOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
  PieChartOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { getTopology } from '../services/api';
import type { RecommendedModule } from '../types';

const allModules = [
  { key: 'basic_stats', name: '基本统计', icon: <BarChartOutlined />, desc: '描述统计、正态性检验、离群值检测' },
  { key: 'process_capability', name: '过程能力', icon: <FundOutlined />, desc: 'Cp/Cpk/Pp/Ppk 能力指数计算' },
  { key: 'spc', name: 'SPC控制图', icon: <LineChartOutlined />, desc: 'I-MR/Xbar-R/Xbar-S 控制图' },
  { key: 'hypothesis', name: '假设检验', icon: <ExperimentOutlined />, desc: 't检验/ANOVA/Mann-Whitney' },
  { key: 'msa', name: '测量系统分析', icon: <ToolOutlined />, desc: 'GR&R ANOVA法 测量系统评估' },
  { key: 'regression', name: '回归分析', icon: <DotChartOutlined />, desc: '线性/岭/Lasso/随机森林回归' },
  { key: 'doe', name: 'DOE实验设计', icon: <ClusterOutlined />, desc: '全因子/部分因子 ANOVA分析' },
  { key: 'time_series', name: '时间序列', icon: <FieldTimeOutlined />, desc: '趋势检测/季节分解/ARIMA' },
  { key: 'multivariate', name: '多变量分析', icon: <HeatMapOutlined />, desc: 'PCA/K-Means/相关矩阵' },
  { key: 'reliability', name: '可靠性分析', icon: <SafetyCertificateOutlined />, desc: 'Weibull分布/可靠度/MTTF' },
  { key: 'nonparametric', name: '非参数分析', icon: <SwapOutlined />, desc: 'Wilcoxon/Kruskal-Wallis' },
  { key: 'chi_square', name: '卡方分析', icon: <PieChartOutlined />, desc: '独立性检验/拟合优度' },
];

const AnalysisTypeSelect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawParams = searchParams.get('params') || '{}';
  const params = JSON.parse(decodeURIComponent(rawParams));

  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState<RecommendedModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>('');
  const [chatInput, setChatInput] = useState('');

  useEffect(() => {
    const fetchTopology = async () => {
      try {
        const topo = await getTopology(params.fileId);
        setRecommendations(topo.recommended_modules.slice(0, 3));
      } catch {
        setRecommendations([
          { module_name: 'basic_stats', display_name: '基本统计', confidence: 0.95, reason: '数据具有连续型特征，适合基本统计分析' },
          { module_name: 'hypothesis', display_name: '假设检验', confidence: 0.82, reason: '检测到多组数据，可进行组间比较' },
          { module_name: 'process_capability', display_name: '过程能力', confidence: 0.75, reason: '数据呈现过程特征，适合过程能力评估' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTopology();
  }, [rawParams]);

  const handleSelectModule = (moduleName: string) => {
    const newParams = encodeURIComponent(
      JSON.stringify({ ...params, module: moduleName })
    );
    navigate(`/analysis/result?params=${newParams}`);
  };

  const handleChat = () => {
    if (!chatInput.trim()) return;
    const input = chatInput.trim().toLowerCase();

    // Keyword matching for analysis modules
    const moduleKeywordMap: Array<{ keywords: string[]; module: string; label: string }> = [
      { keywords: ['基本统计', '描述统计', '正态', '离群', '均值', '标准差'], module: 'basic_stats', label: '基本统计' },
      { keywords: ['过程能力', 'cpk', 'ppk', '能力指数'], module: 'process_capability', label: '过程能力' },
      { keywords: ['控制图', 'spc', 'xbar', 'mr', '控制'], module: 'spc', label: 'SPC控制图' },
      { keywords: ['假设检验', 't检验', 'anova', '显著', '差异', '比较均值'], module: 'hypothesis', label: '假设检验' },
      { keywords: ['测量系统', 'msa', 'grr', 'gr&r', '量具'], module: 'msa', label: '测量系统分析' },
      { keywords: ['回归', '预测', '线性', '相关性'], module: 'regression', label: '回归分析' },
      { keywords: ['实验设计', 'doe', '因子', '因素'], module: 'doe', label: 'DOE实验设计' },
      { keywords: ['时间序列', '趋势', '季节', 'arima', '预报'], module: 'time_series', label: '时间序列' },
      { keywords: ['多变量', 'pca', '聚类', 'kmeans', '相关矩阵', '降维'], module: 'multivariate', label: '多变量分析' },
      { keywords: ['可靠性', 'weibull', '寿命', 'mttf', '失效'], module: 'reliability', label: '可靠性分析' },
      { keywords: ['非参数', 'wilcoxon', 'kruskal', '秩和'], module: 'nonparametric', label: '非参数分析' },
      { keywords: ['卡方', '独立性', '拟合优度', '列联'], module: 'chi_square', label: '卡方分析' },
    ];

    let matched: { module: string; label: string } | null = null;
    for (const entry of moduleKeywordMap) {
      if (entry.keywords.some((kw) => input.includes(kw))) {
        matched = { module: entry.module, label: entry.label };
        break;
      }
    }

    if (matched) {
      message.success(`已匹配到「${matched.label}」，正在跳转...`);
      handleSelectModule(matched.module);
    } else {
      message.info('未能识别分析类型，请尝试使用关键词，如：基本统计、假设检验、回归分析等');
    }

    setChatInput('');
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px 0' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        返回
      </Button>

      <div style={{ marginBottom: 32 }}>
        <h2 style={{ color: '#061b31', fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
          AI 智能匹配分析
          <Tag color="purple" style={{ marginLeft: 8, fontSize: 12 }}>
            只推荐三个
          </Tag>
        </h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" tip="AI 正在分析数据拓扑，匹配最佳分析方法..." />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {recommendations.map((rec, index) => {
              const module = allModules.find((m) => m.key === rec.module_name);
              return (
                <Card
                  key={rec.module_name}
                  hoverable
                  onClick={() => handleSelectModule(rec.module_name)}
                  style={{
                    borderColor: index === 0 ? '#533afd' : undefined,
                    borderWidth: index === 0 ? 2 : 1,
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: index === 0
                          ? 'linear-gradient(135deg, #533afd 0%, #6b54fd 100%)'
                          : '#f0f2f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 12px',
                        fontSize: 24,
                        color: index === 0 ? '#fff' : '#533afd',
                      }}
                    >
                      {module?.icon || <BarChartOutlined />}
                    </div>
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: '#061b31', marginBottom: 4 }}>
                      {rec.display_name}
                    </h3>
                    <Tag color="blue" style={{ marginBottom: 8 }}>
                      匹配度 {Math.round(rec.confidence * 100)}%
                    </Tag>
                    <p style={{ fontSize: 12, color: '#666', margin: 0 }}>{rec.reason}</p>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 32,
          display: 'flex',
          gap: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Input
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="描述您的分析需求，例如：我想检验两组数据均值是否有显著差异"
          onPressEnter={handleChat}
          size="large"
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<SendOutlined />} size="large" onClick={handleChat}>
          发送
        </Button>
      </div>

      <div>
        <h2 style={{ color: '#061b31', fontSize: 20, fontWeight: 600, marginBottom: 16 }}>
          全部可用分析
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 16,
          }}
        >
          {allModules.map((module) => {
            const isSelected = selectedModule === module.key;
            return (
              <div
                key={module.key}
                onClick={() => handleSelectModule(module.key)}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#533afd';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = '#e8e8e8';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
                  }
                }}
                style={{
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  background: isSelected ? 'rgba(102, 126, 234, 0.08)' : '#fff',
                  border: `2px solid ${isSelected ? '#533afd' : '#e8e8e8'}`,
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  boxShadow: isSelected
                    ? '0 4px 16px rgba(102, 126, 234, 0.2)'
                    : '0 2px 8px rgba(0,0,0,0.06)',
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                  padding: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 36,
                    color: isSelected ? '#533afd' : '#8c8c8c',
                    transition: 'color 0.25s ease',
                  }}
                >
                  {module.icon}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isSelected ? '#533afd' : '#333',
                    textAlign: 'center',
                    transition: 'color 0.25s ease',
                  }}
                >
                  {module.name}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: '#999',
                    textAlign: 'center',
                    lineHeight: 1.4,
                  }}
                >
                  {module.desc}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AnalysisTypeSelect;
